import React, { useEffect, useState } from 'react';
import { Container, Box, Typography, Paper, CircularProgress, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const defaultText = `My Local Force (“we”, “our”, or “us”) is committed to protecting the privacy and security of our users (“you”, “your"). This Privacy Policy explains how we collect, use, store, and protect your personal and device information when you use the My Local Force mobile application, website, and related services.

By using our platform, you agree to the terms outlined in this policy.`;

const renderParagraphs = (text) => {
  if (!text) return null;
  return text.split(/\n\n+/).map((p, idx) => (
    <Typography paragraph key={idx} sx={{ whiteSpace: 'pre-line' }}>
      {p}
    </Typography>
  ));
};

// renderSections moved into component to allow controlled expansion behavior

const formatTimestamp = (ts) => {
  if (!ts) return '[Insert Date]';
  try {
    const d = ts.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date(ts);
    return d.toLocaleDateString();
  } catch (e) {
    return '[Insert Date]';
  }
};

const PrivacyPolicy = () => {
  const [loading, setLoading] = useState(true);
  const [policy, setPolicy] = useState(null);
  const [expandedSection, setExpandedSection] = useState(0);
  const [expandedSubs, setExpandedSubs] = useState({});

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const ref = doc(db, 'settings', 'privacyPolicy');
        const snap = await getDoc(ref);
        if (mounted) {
          if (snap.exists()) {
            const data = snap.data();
            // If legacy content string, convert to sections for rendering
            if (data.sections && Array.isArray(data.sections)) {
              setPolicy(data);
            } else if (typeof data.content === 'string') {
              setPolicy({ sections: [{ id: 'legacy', heading: 'Privacy and Data Protection', subheading: '', content: data.content }], lastUpdated: data.lastUpdated || null });
            } else {
              setPolicy({ sections: [{ id: 'default', heading: 'Privacy and Data Protection', subheading: '', content: defaultText }], lastUpdated: null });
            }
          } else {
            setPolicy({ sections: [{ id: 'default', heading: 'Privacy and Data Protection', subheading: '', content: defaultText }], lastUpdated: null });
          }
        }
      } catch (err) {
        console.error('Failed to load privacy policy', err);
        if (mounted) setPolicy({ sections: [{ id: 'default', heading: 'Privacy and Data Protection', subheading: '', content: defaultText }], lastUpdated: null });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, []);

  // Helper to build groups from blocks (used for nested accordions)
  const buildGroupsFromBlocks = (blocks) => {
    const groups = [];
    let current = { type: 'root', paragraphs: [] };
    (blocks || []).forEach((b) => {
      if (b.type === 'subheading') {
        if ((current.type === 'root' && current.paragraphs.length > 0) || (current.type === 'subheading' && (current.title || current.paragraphs.length > 0))) {
          groups.push(current);
        }
        current = { type: 'subheading', title: b.content, paragraphs: [] };
      } else {
        current.paragraphs.push(b.content);
      }
    });
    if (current && (current.paragraphs.length > 0 || current.title)) groups.push(current);
    return groups;
  };

  // When policy loads, open first section and its first subheading (if present)
  useEffect(() => {
    if (policy && Array.isArray(policy.sections) && policy.sections.length > 0) {
      setExpandedSection(0);
      const firstBlocks = policy.sections[0].blocks && Array.isArray(policy.sections[0].blocks) ? policy.sections[0].blocks : [];
      const groups = buildGroupsFromBlocks(firstBlocks);
      const firstSubIndex = groups.findIndex(g => g.type === 'subheading');
      if (firstSubIndex !== -1) {
        // determine subOnlyIndex (zero-based among subheading groups)
        const subOnlyIndex = groups.slice(0, firstSubIndex + 1).filter(x => x.type === 'subheading').length - 1;
        setExpandedSubs(prev => ({ ...prev, 0: subOnlyIndex }));
      }
    }
  }, [policy]);

  // When a top-level section is expanded, auto-open its first subheading if present
  useEffect(() => {
    if (policy && expandedSection !== false && expandedSection != null && Array.isArray(policy.sections) && policy.sections[expandedSection]) {
      const blocks = policy.sections[expandedSection].blocks && Array.isArray(policy.sections[expandedSection].blocks) ? policy.sections[expandedSection].blocks : [];
      const groups = buildGroupsFromBlocks(blocks);
      const firstSubIndex = groups.findIndex(g => g.type === 'subheading');
      if (firstSubIndex !== -1) {
        const subOnlyIndex = groups.slice(0, firstSubIndex + 1).filter(x => x.type === 'subheading').length - 1;
        setExpandedSubs(prev => ({ ...prev, [expandedSection]: subOnlyIndex }));
      } else {
        // clear any expanded sub for this section
        setExpandedSubs(prev => {
          const copy = { ...prev };
          delete copy[expandedSection];
          return copy;
        });
      }
    }
  }, [expandedSection, policy]);

  // Render sections with nested accordions, controlled by expandedSection and expandedSubs
  const renderSections = (sections) => {
    if (!sections || !Array.isArray(sections) || sections.length === 0) return null;
    return sections.map((s, idx) => {
      const headingNumber = idx + 1;
      const blocks = s.blocks && Array.isArray(s.blocks) ? s.blocks : null;

      // If no structured blocks, fall back to legacy content rendering
      if (!blocks) {
        return (
          <Accordion key={s.id || idx} sx={{ mb: 2, '&:before': { display: 'none' } }} disableGutters expanded={expandedSection === idx} onChange={(e, isExp) => setExpandedSection(isExp ? idx : false)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: '#E8F0FF', color: '#2b7be4', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                  {headingNumber}
                </Box>
                <Typography variant="h6">{s.heading || `Section ${headingNumber}`}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {renderParagraphs(s.content)}
            </AccordionDetails>
          </Accordion>
        );
      }

      const groups = buildGroupsFromBlocks(blocks);

      return (
        <Accordion key={s.id || idx} sx={{ mb: 2, borderRadius: 2, backgroundColor: '#F3F9FF', boxShadow: 'none', border: '1px solid #E6F0FF', '&:before': { display: 'none' } }} disableGutters expanded={expandedSection === idx} onChange={(e, isExp) => setExpandedSection(isExp ? idx : false)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: '#E8F0FF', color: '#2b7be4', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                <InfoOutlinedIcon sx={{ color: '#2b7be4', fontSize: 18 }} />
              </Box>
              <Typography variant="h6" sx={{ color: '#2b7be4', fontWeight: 700 }}>{`${headingNumber}. ${s.heading || `Section ${headingNumber}`}`}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
              {(() => {
              let subOnlyCount = -1;
              return groups.map((g, gi) => {
                if (g.type === 'subheading' && g.title) {
                  subOnlyCount += 1;
                  const subIdx = subOnlyCount;
                  const isExpanded = expandedSubs[idx] === subIdx;
                  return (
                    <Accordion key={`${s.id || idx}-sub-${gi}`} sx={{ mb: 1, borderRadius: 1, backgroundColor: '#FFFFFF', boxShadow: 'none', '&:before': { display: 'none' } }} disableGutters expanded={!!isExpanded} onChange={(e, isExp) => setExpandedSubs(prev => ({ ...prev, [idx]: isExp ? subIdx : null }))}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#2b7be4' }} />}>
                        <Typography variant="subtitle1" color="#2b7be4" sx={{ fontWeight: 700 }}>
                          {`${headingNumber}.${subIdx + 1} ${g.title}`}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {g.paragraphs.map((p, pi) => (
                          <Typography paragraph sx={{ whiteSpace: 'pre-line' }} key={pi}>
                            {p}
                          </Typography>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  );
                }

                // root paragraphs (not under any subheading)
                return (
                  <Box key={`${s.id || idx}-root-${gi}`} sx={{ mb: 2 }}>
                    {g.paragraphs.map((p, pi) => (
                      <Typography paragraph sx={{ whiteSpace: 'pre-line' }} key={pi}>
                        {p}
                      </Typography>
                    ))}
                  </Box>
                );
              });
            })()}
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={6} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ background: 'linear-gradient(90deg,#2b7be4,#5b3bd6)', color: '#fff', p: 3.5 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <InfoOutlinedIcon sx={{ color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{policy?.title || 'Privacy Policy'}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.95, mt: 0.5 }}>{policy?.description || 'Welcome to My Local Force. Please review these Privacy Policy carefully.'}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.95, mt: 1, display: 'block' }}>
                Last Updated: {policy ? formatTimestamp(policy.lastUpdated) : '[Insert Date]'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: 4 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {renderSections(policy?.sections || [{ id: 'default', heading: 'Privacy and Data Protection', content: defaultText }])}
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;
